import type { PaymentAction as V6PaymentAction } from '@adyen/adyen-web-v6';
import type { PaymentAction as LegacyPaymentAction } from '@adyen/adyen-web/dist/types/types';
import { useEppoExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags';
import { useLegacyAdyenSCA } from './legacy_adyen_sca_hook';
import { useV6AdyenSCA } from './adyen_sca_hook_v6';
import { useAdyenEnvironment } from '@reverbdotcom/commons/src/adyen_environment_hook';

// In compliance with PSD2 onward,
// 3DS is one of many implementations of
// Strong Customer Authentication (SCA)
export enum SCAType {
  Fingerprint = 'threeDS2Fingerprint',
  Challenge = 'threeDS2Challenge',
  Redirect = 'redirect',
}

// In Checkout API v67 onward, the 3DS2 subtypes
// are "challenge" and "fingerprint". These both
// have a parent action.type == "threeDS2"
export enum SCASubType {
  Fingerprint = 'fingerprint',
  Challenge = 'challenge',
}

// In Checkout API v67 onward, the state.data.details we get
// from Adyen will be "threeDSResult"
export interface ThreeDS2ResultData {
  details: { 'threeDSResult': string };
  paymentData: string;
}

export interface SCAState {
  data: ThreeDS2ResultData;
}

// For 3DS2 configuration see https://docs.adyen.com/online-payments/3d-secure/native-3ds2/web-component
// For 3DS1 redirect configuration see https://docs.adyen.com/online-payments/3d-secure/redirect-3ds2-3ds1/web-component
export function useAdyenSCA({ selector, action, onAdditionalDetails }:
{ selector: string; action: V6PaymentAction | LegacyPaymentAction, onAdditionalDetails(state:SCAState): void },
) {
  const useAdyenV6 = useEppoExpEnabled({ flagKey: eppoFlags.ADYEN_WEB_SIX });
  const { environment, clientKey } = useAdyenEnvironment();

  useV6AdyenSCA({ selector, action: action as V6PaymentAction, onAdditionalDetails, enabled: useAdyenV6, clientKey, environment });
  useLegacyAdyenSCA({ selector, action: action as LegacyPaymentAction, onAdditionalDetails, enabled: !useAdyenV6, clientKey, environment });
}
